/**
 * カート販売期間チェック機能
 * 
 * 機能:
 * - カート内商品の販売期間をリアルタイムでチェック
 * - 販売期間外商品がある場合のチェックアウトボタン制御
 * - 販売期間外商品の視覚的表示と通知
 * - cart-group-restriction.jsとの連携
 * - アクセシビリティ対応
 */

(function() {
  'use strict';

  // デバッグログ機能
  const DEBUG = window.location.hostname === 'localhost' || window.location.search.includes('debug=true');
  function debugLog(message, data = null) {
    if (DEBUG) {
      if (Shopify.designMode) console.log('[CartSalesPeriod]', message, data || '');
    }
  }

  class CartSalesPeriodChecker {
    constructor() {
      this.salesPeriodData = null;
      this.checkoutButton = null;
      this.isInitialized = false;
      this.checkInterval = null;
      this.buttonMonitorInterval = null;
      this.mutationObserver = null;
      this.init();
    }

    // 初期化
    init() {
      debugLog('初期化開始');
      
      try {
        this.loadSalesPeriodData();
        this.findElements();
        this.updateCartDisplay();
        this.attachEventListeners();
        this.startPeriodicCheck();
        
        // 他のスクリプトによる干渉を防ぐため、少し遅延してから再チェック
        setTimeout(() => {
          this.updateCartDisplay();
          debugLog('遅延チェック完了');
        }, 1000);
        
        // さらに遅延してもう一度チェック（Shopifyの非同期処理対応）
        setTimeout(() => {
          this.updateCartDisplay();
          debugLog('最終遅延チェック完了');
        }, 3000);
        
        this.isInitialized = true;
        debugLog('初期化完了', this.getDebugInfo());
        
        // 他のスクリプトに初期化完了を通知
        this.dispatchInitEvent();
        
      } catch (error) {
        if (Shopify.designMode) console.error('[CartSalesPeriod] 初期化エラー:', error);
      }
    }

    // 販売期間データの読み込み
    loadSalesPeriodData() {
      const dataElement = document.getElementById('cart-sales-period-data');
      if (dataElement) {
        try {
          this.salesPeriodData = JSON.parse(dataElement.textContent);
          debugLog('販売期間データ読み込み完了', this.salesPeriodData);
        } catch (error) {
          if (Shopify.designMode) console.error('[CartSalesPeriod] データ解析エラー:', error);
          this.salesPeriodData = { hasInvalidItems: false, invalidItems: [], invalidItemsInfo: [] };
        }
      } else {
        debugLog('販売期間データが見つからない、デフォルトデータを使用');
        this.salesPeriodData = { hasInvalidItems: false, invalidItems: [], invalidItemsInfo: [] };
      }
    }

    // DOM要素の検索
    findElements() {
      // チェックアウトボタンの検索
      this.checkoutButton = document.querySelector('#checkout') || 
                           document.querySelector('.cart__checkout-button') ||
                           document.querySelector('button[name="checkout"]');
      
      debugLog('要素検索完了', {
        checkoutButton: !!this.checkoutButton
      });
    }

    // カート表示の更新
    updateCartDisplay() {
      if (!this.salesPeriodData) return;

      debugLog('カート表示更新開始', {
        hasInvalidItems: this.salesPeriodData.hasInvalidItems,
        invalidItemsCount: this.salesPeriodData.invalidItems.length
      });

      // チェックアウトボタンの状態更新
      this.updateCheckoutButton();
      
      // 販売期間外商品の視覚的表示
      this.updateInvalidItemsDisplay();
      
      // エラーメッセージの表示
      this.updateErrorMessages();
    }

    // チェックアウトボタンの状態更新
    updateCheckoutButton() {
      if (!this.checkoutButton) return;

      debugLog('チェックアウトボタン状態更新開始', {
        hasInvalidItems: this.salesPeriodData.hasInvalidItems,
        hasOtherRestrictions: this.hasOtherRestrictions(),
        currentDisabled: this.checkoutButton.disabled
      });

      if (this.salesPeriodData.hasInvalidItems) {
        // 販売期間外商品がある場合 - 強制的に無効化
        this.forceDisableButton();
        
        // 継続的な監視を開始
        this.startButtonMonitoring();
        
      } else {
        // 販売期間内の商品のみの場合
        this.checkoutButton.removeAttribute('data-sales-period-restricted');
        
        const originalText = this.checkoutButton.getAttribute('data-original-text');
        if (originalText) {
          this.checkoutButton.textContent = originalText;
          this.checkoutButton.removeAttribute('data-original-text');
        }
        
        // 他の制限がない場合のみ有効化
        if (!this.hasOtherRestrictions()) {
          this.checkoutButton.disabled = false;
          this.checkoutButton.removeAttribute('aria-describedby');
        }
        
        debugLog('チェックアウトボタン制限解除完了');
        
        // 監視を停止
        this.stopButtonMonitoring();
      }
    }

    // 他の制限チェック（カートグループ制限など）
    hasOtherRestrictions() {
      // カートが空の場合
      const cartItems = document.querySelectorAll('.cart-item, [data-cart-item]');
      if (!cartItems.length) {
        debugLog('カートが空のため他の制限あり');
        return true;
      }

      // cart-group-restriction.jsによる制限チェック（安全な方法）
      try {
        if (window.cartGroupRestriction && 
            typeof window.cartGroupRestriction.hasRestriction === 'function') {
          const hasGroupRestriction = window.cartGroupRestriction.hasRestriction();
          debugLog('カートグループ制限状態:', hasGroupRestriction);
          return hasGroupRestriction;
        }
      } catch (error) {
        debugLog('カートグループ制限チェックエラー:', error.message);
      }

      debugLog('他の制限なし');
      return false;
    }

    // 販売期間外商品の視覚的表示
    updateInvalidItemsDisplay() {
      // カート内の商品行をすべて取得
      const cartItems = document.querySelectorAll('.cart-item, [data-cart-item]');
      
      cartItems.forEach(item => {
        this.updateCartItemDisplay(item);
      });
    }

    // 個別カート商品の表示更新
    updateCartItemDisplay(cartItem) {
      if (!cartItem) return;

      // 商品のキーを取得（data-line-item-key属性から）
      const itemKey = cartItem.getAttribute('data-line-item-key') || 
                      cartItem.querySelector('[data-line-item-key]')?.getAttribute('data-line-item-key');
      
      if (!itemKey) return;

      const isInvalid = this.salesPeriodData.invalidItems.includes(itemKey);
      
      if (isInvalid) {
        // 販売期間外の商品をマーク
        cartItem.classList.add('cart-item--sales-period-expired');
        
        // 警告メッセージの追加
        this.addWarningMessage(cartItem, itemKey);
        
        debugLog('販売期間外商品をマーク', { itemKey });
        
      } else {
        // 販売期間内の商品から警告を削除
        cartItem.classList.remove('cart-item--sales-period-expired');
        this.removeWarningMessage(cartItem);
      }
    }

    // 警告メッセージの追加
    addWarningMessage(cartItem, itemKey) {
      // 既存の警告メッセージがあるかチェック
      let warningElement = cartItem.querySelector('.sales-period-warning');
      
      if (!warningElement) {
        warningElement = document.createElement('div');
        warningElement.className = 'sales-period-warning';
        warningElement.setAttribute('role', 'alert');
        
        // 商品情報から詳細な警告メッセージを取得
        const itemInfo = this.getItemInfo(itemKey);
        warningElement.innerHTML = `
          <div class="sales-period-warning__content">
            <strong class="sales-period-warning__title">販売期間外</strong>
            <p class="sales-period-warning__message">${itemInfo.statusMessage}</p>
            <button type="button" class="sales-period-warning__remove" data-item-key="${itemKey}">
              カートから削除
            </button>
          </div>
        `;
        
        // 適切な位置に挿入
        const insertPoint = cartItem.querySelector('.cart-item__details') || 
                           cartItem.querySelector('.cart-item__media') || 
                           cartItem;
        insertPoint.appendChild(warningElement);
        
        // 削除ボタンのイベントリスナー
        const removeButton = warningElement.querySelector('.sales-period-warning__remove');
        if (removeButton) {
          removeButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.removeItemFromCart(itemKey);
          });
        }
      }
    }

    // 警告メッセージの削除
    removeWarningMessage(cartItem) {
      const warningElement = cartItem.querySelector('.sales-period-warning');
      if (warningElement) {
        warningElement.remove();
      }
    }

    // 商品情報の取得
    getItemInfo(itemKey) {
      const itemInfoArray = this.salesPeriodData.invalidItemsInfo || [];
      
      for (const info of itemInfoArray) {
        const parts = info.split('|');
        if (parts[2] === itemKey) {
          return {
            title: parts[0],
            statusMessage: parts[1],
            key: parts[2]
          };
        }
      }
      
      return {
        title: '商品',
        statusMessage: '販売期間外です',
        key: itemKey
      };
    }

    // エラーメッセージの更新
    updateErrorMessages() {
      const cartErrorsContainer = document.getElementById('cart-errors');
      if (!cartErrorsContainer) return;

      if (this.salesPeriodData.hasInvalidItems) {
        // 販売期間外商品のエラーメッセージを表示
        const existingError = cartErrorsContainer.querySelector('.sales-period-error');
        if (!existingError) {
          const errorElement = document.createElement('div');
          errorElement.className = 'sales-period-error cart__error';
          errorElement.setAttribute('role', 'alert');
          errorElement.innerHTML = `
            <h3>販売期間外の商品があります</h3>
            <p>カートに販売期間外の商品が含まれているため、購入手続きを進めることができません。該当商品をカートから削除してください。</p>
          `;
          cartErrorsContainer.appendChild(errorElement);
        }
      } else {
        // エラーメッセージを削除
        const existingError = cartErrorsContainer.querySelector('.sales-period-error');
        if (existingError) {
          existingError.remove();
        }
      }
    }

    // カートからの商品削除
    removeItemFromCart(itemKey) {
      debugLog('商品削除実行', { itemKey });
      
      // Shopifyのカート更新API呼び出し
      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      })
      .then(response => response.json())
      .then(cart => {
        debugLog('商品削除成功', cart);
        
        // ページリロードまたはカート更新
        window.location.reload();
      })
      .catch(error => {
        if (Shopify.designMode) console.error('[CartSalesPeriod] 商品削除エラー:', error);
        
        // エラー時はページリロード
        window.location.reload();
      });
    }

    // イベントリスナーの設定
    attachEventListeners() {
      // カート更新イベントの監視（複数のイベント形式に対応）
      const cartUpdateEvents = ['cart:updated', 'cart:changed', 'cart:refresh'];
      cartUpdateEvents.forEach(eventType => {
        document.addEventListener(eventType, () => {
          debugLog(`${eventType} イベント検出`);
          setTimeout(() => {
            this.loadSalesPeriodData();
            this.updateCartDisplay();
          }, 100);
        });
      });

      // Shopify Section Rendering API対応
      document.addEventListener('shopify:section:load', () => {
        debugLog('セクション読み込み検出');
        setTimeout(() => {
          this.findElements();
          this.loadSalesPeriodData();
          this.updateCartDisplay();
        }, 200);
      });

      // ページ可視性変更時の再チェック
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          debugLog('ページ可視性復帰、販売期間再チェック');
          this.recheckSalesPeriod();
        }
      });

      // cart-group-restriction.jsとの連携
      window.addEventListener('cart-restriction-updated', () => {
        debugLog('カート制限更新イベント受信');
        this.updateCheckoutButton();
      });

      // 手動再チェック用のカスタムイベント
      window.addEventListener('sales-period-recheck', () => {
        debugLog('手動再チェックイベント受信');
        this.recheckSalesPeriod();
      });

      // DOMの変更監視（MutationObserver）
      this.setupMutationObserver();
    }

    // DOMの変更を監視してボタンの状態変更を検出
    setupMutationObserver() {
      if (!this.checkoutButton) return;

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'disabled' || mutation.attributeName === 'class')) {
            
            // 販売期間外商品があるのにボタンが有効化された場合
            if (this.salesPeriodData.hasInvalidItems && !this.checkoutButton.disabled) {
              debugLog('MutationObserver: ボタンが外部により有効化検出、再制御');
              setTimeout(() => {
                this.forceDisableButton();
              }, 50);
            }
          }
        });
      });

      observer.observe(this.checkoutButton, {
        attributes: true,
        attributeFilter: ['disabled', 'class', 'aria-describedby']
      });

      this.mutationObserver = observer;
      debugLog('MutationObserver設定完了');
    }

    // ボタン状態の継続的な監視開始
    startButtonMonitoring() {
      if (this.buttonMonitorInterval) {
        clearInterval(this.buttonMonitorInterval);
      }

      this.buttonMonitorInterval = setInterval(() => {
        if (this.checkoutButton && this.salesPeriodData.hasInvalidItems) {
          // 販売期間外商品があるのにボタンが有効になっている場合
          if (!this.checkoutButton.disabled || !this.checkoutButton.getAttribute('data-sales-period-restricted')) {
            debugLog('ボタンが他のスクリプトにより有効化された、再制御実行');
            this.forceDisableButton();
          }
        }
      }, 500); // 0.5秒間隔で監視

      debugLog('ボタン監視開始');
    }

    // ボタン状態の監視停止
    stopButtonMonitoring() {
      if (this.buttonMonitorInterval) {
        clearInterval(this.buttonMonitorInterval);
        this.buttonMonitorInterval = null;
        debugLog('ボタン監視停止');
      }
    }

    // ボタンの強制無効化
    forceDisableButton() {
      if (!this.checkoutButton) return;

      this.checkoutButton.disabled = true;
      this.checkoutButton.setAttribute('data-sales-period-restricted', 'true');
      
      // スクリーンリーダー用の説明要素を確認・作成
      this.ensureAccessibilityElement();
      this.checkoutButton.setAttribute('aria-describedby', 'sales-period-checkout-restriction');
      
      // テキストも強制的に更新
      if (!this.checkoutButton.textContent.includes('販売期間外')) {
        const currentText = this.checkoutButton.textContent;
        this.checkoutButton.setAttribute('data-original-text', currentText);
        this.checkoutButton.textContent = '販売期間外商品あり';
      }

      debugLog('ボタン強制無効化実行');
    }

    // スクリーンリーダー用の要素を確保
    ensureAccessibilityElement() {
      let restrictionElement = document.getElementById('sales-period-checkout-restriction');
      
      if (!restrictionElement) {
        restrictionElement = document.createElement('div');
        restrictionElement.id = 'sales-period-checkout-restriction';
        restrictionElement.className = 'sr-only';
        restrictionElement.textContent = 'カートに販売期間外の商品が含まれているため、購入手続きを進めることができません。該当商品をカートから削除してください。';
        
        // main-cart-footerの近くに配置
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) {
          cartFooter.appendChild(restrictionElement);
        } else {
          document.body.appendChild(restrictionElement);
        }
        
        debugLog('スクリーンリーダー用要素を動的作成');
      }
    }

    // 定期的な販売期間チェックの開始
    startPeriodicCheck() {
      // 既存のインターバルをクリア
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
      }

      // 1分間隔でチェック
      this.checkInterval = setInterval(() => {
        this.recheckSalesPeriod();
      }, 60000);
      
      debugLog('定期チェック開始（1分間隔）');
    }

    // 販売期間の再チェック
    recheckSalesPeriod() {
      debugLog('販売期間再チェック実行');
      
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // カートデータから販売期間をチェック
          return this.checkCartSalesPeriods(cart);
        })
        .then(needsUpdate => {
          if (needsUpdate) {
            debugLog('販売期間変更検出、ページ更新');
            window.location.reload();
          }
        })
        .catch(error => {
          if (Shopify.designMode) console.error('[CartSalesPeriod] 再チェックエラー:', error);
        });
    }

    // カート商品の販売期間チェック
    async checkCartSalesPeriods(cart) {
      const currentTime = Math.floor(Date.now() / 1000);
      let hasChanges = false;
      
      for (const item of cart.items) {
        // 商品ハンドルから商品データを取得（必要に応じて実装）
        const salesPeriodActive = await this.checkItemSalesPeriod(item, currentTime);
        
        const wasInvalid = this.salesPeriodData.invalidItems.includes(item.key);
        const isNowInvalid = !salesPeriodActive;
        
        if (wasInvalid !== isNowInvalid) {
          hasChanges = true;
          break;
        }
      }
      
      return hasChanges;
    }

    // 個別商品の販売期間チェック
    async checkItemSalesPeriod(item, currentTime) {
      // この実装は商品メタフィールドアクセスが必要
      // 簡易実装として、現在の状態をそのまま返す
      return !this.salesPeriodData.invalidItems.includes(item.key);
    }

    // 初期化完了イベントの送信
    dispatchInitEvent() {
      const event = new CustomEvent('cart-sales-period-ready', {
        detail: {
          instance: this,
          hasInvalidItems: this.salesPeriodData.hasInvalidItems,
          invalidItemsCount: this.salesPeriodData.invalidItems.length
        }
      });
      window.dispatchEvent(event);
    }

    // スクリーンリーダー用のアナウンス
    announceToScreenReader(message) {
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'assertive');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.style.position = 'absolute';
      announcement.style.left = '-10000px';
      announcement.style.width = '1px';
      announcement.style.height = '1px';
      announcement.style.overflow = 'hidden';
      announcement.textContent = message;
      
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        if (announcement.parentNode) {
          announcement.parentNode.removeChild(announcement);
        }
      }, 1000);
    }

    // 外部からの状態確認用メソッド
    hasInvalidSalesPeriodItems() {
      return this.salesPeriodData ? this.salesPeriodData.hasInvalidItems : false;
    }

    getInvalidItems() {
      return this.salesPeriodData ? this.salesPeriodData.invalidItems : [];
    }

    // デバッグ用の状態確認
    getDebugInfo() {
      return {
        salesPeriodData: this.salesPeriodData,
        hasCheckoutButton: !!this.checkoutButton,
        checkoutButtonDisabled: this.checkoutButton ? this.checkoutButton.disabled : null,
        checkoutButtonText: this.checkoutButton ? this.checkoutButton.textContent : null,
        salesPeriodRestricted: this.checkoutButton ? this.checkoutButton.getAttribute('data-sales-period-restricted') : null,
        isInitialized: this.isInitialized,
        checkInterval: !!this.checkInterval,
        buttonMonitorInterval: !!this.buttonMonitorInterval,
        hasOtherRestrictions: this.hasOtherRestrictions()
      };
    }

    // リソースのクリーンアップ
    destroy() {
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
      if (this.buttonMonitorInterval) {
        clearInterval(this.buttonMonitorInterval);
        this.buttonMonitorInterval = null;
      }
      if (this.mutationObserver) {
        this.mutationObserver.disconnect();
        this.mutationObserver = null;
      }
      this.isInitialized = false;
      debugLog('リソースクリーンアップ完了');
    }
  }

  // CSS スタイルの追加
  if (!document.querySelector('#cart-sales-period-styles')) {
    const style = document.createElement('style');
    style.id = 'cart-sales-period-styles';
    style.textContent = `
      /* 販売期間外商品のスタイル */
      .cart-item--sales-period-expired {
        position: relative;
        opacity: 0.7;
      }
      
      .cart-item--sales-period-expired::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: repeating-linear-gradient(
          45deg,
          transparent,
          transparent 10px,
          rgba(220, 53, 69, 0.1) 10px,
          rgba(220, 53, 69, 0.1) 20px
        );
        pointer-events: none;
        z-index: 1;
      }
      
      .sales-period-warning {
        background: rgba(220, 53, 69, 0.1);
        border: 2px solid rgba(220, 53, 69, 0.3);
        border-radius: 8px;
        padding: 16px;
        margin: 12px 0;
        position: relative;
        z-index: 2;
      }
      
      .sales-period-warning__title {
        color: #dc3545;
        font-weight: 600;
        font-size: 1.6rem;
        margin-bottom: 8px;
        display: block;
      }
      
      .sales-period-warning__message {
        color: #721c24;
        font-size: 1.4rem;
        margin-bottom: 12px;
        line-height: 1.4;
      }
      
      .sales-period-warning__remove {
        background: #dc3545;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 6px;
        font-size: 1.3rem;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }
      
      .sales-period-warning__remove:hover {
        background: #c82333;
      }
      
      .sales-period-warning__remove:focus {
        outline: 3px solid rgba(220, 53, 69, 0.5);
        outline-offset: 2px;
      }
      
      .sales-period-error {
        background: rgba(220, 53, 69, 0.1);
        border: 2px solid rgba(220, 53, 69, 0.3);
        border-radius: 8px;
        padding: 20px;
        margin: 16px 0;
      }
      
      .sales-period-error h3 {
        color: #dc3545;
        font-size: 1.8rem;
        font-weight: 600;
        margin: 0 0 12px 0;
      }
      
      .sales-period-error p {
        color: #721c24;
        font-size: 1.5rem;
        margin: 0;
        line-height: 1.5;
      }
      
      /* チェックアウトボタンの無効化スタイル */
      .cart__checkout-button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
      
      /* モバイル対応 */
      @media screen and (max-width: 749px) {
        .sales-period-warning {
          padding: 12px;
          margin: 8px 0;
        }
        
        .sales-period-warning__title {
          font-size: 1.4rem;
        }
        
        .sales-period-warning__message {
          font-size: 1.3rem;
        }
        
        .sales-period-warning__remove {
          font-size: 1.2rem;
          padding: 6px 12px;
        }
      }
      
      /* アニメーション削減設定 */
      @media (prefers-reduced-motion: reduce) {
        .sales-period-warning__remove {
          transition: none;
        }
      }
    `;
    document.head.appendChild(style);
  }

  // グローバルインスタンス
  let cartSalesPeriodChecker = null;

  // 初期化関数
  function initCartSalesPeriodChecker() {
    // カートページでのみ実行
    if (document.querySelector('#main-cart-footer') || document.querySelector('.cart')) {
      cartSalesPeriodChecker = new CartSalesPeriodChecker();
      
      // グローバルアクセス用（デバッグ・外部連携用）
      window.cartSalesPeriodChecker = cartSalesPeriodChecker;
    }
  }

  // DOMContentLoaded時に初期化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartSalesPeriodChecker);
  } else {
    initCartSalesPeriodChecker();
  }

  // cart-group-restriction.jsとの連携
  window.addEventListener('cart-group-restriction-ready', function() {
    debugLog('cart-group-restriction.js との連携開始');
    
    // 販売期間制限をカートグループ制限に通知
    if (cartSalesPeriodChecker && cartSalesPeriodChecker.hasInvalidSalesPeriodItems()) {
      const event = new CustomEvent('sales-period-restriction', {
        detail: {
          restricted: true,
          reason: 'sales-period-expired',
          message: '販売期間外の商品がカートに含まれています'
        }
      });
      window.dispatchEvent(event);
    }
  });

  // ページアンロード時のクリーンアップ
  window.addEventListener('beforeunload', function() {
    if (cartSalesPeriodChecker) {
      cartSalesPeriodChecker.destroy();
    }
  });

})();